import { memo, SyntheticEvent } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useSecurity } from '@dfl/react-security';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { IProductStockItem } from 'modules/inventory/product-stock/interfaces/IStockResponse';
import { STOCK_PERMISSIONS } from 'modules/inventory/product-stock/constants/stock.permissions';
import { WarehouseAreaService } from 'modules/inventory/settings/warehouse-area/services';
import { WAREHOUSE_AREAS_LIST_KEY } from 'modules/inventory/settings/warehouse-area/constants';
import { IWarehouseArea } from 'modules/inventory/settings/warehouse-area/interfaces';
import useProductStockUpdateWarehouseArea from 'modules/inventory/product-stock/hooks/useProductStockUpdateWarehouseArea';

type ProductWarehouseAreaProps = {
  rowId: string;
  record: IProductStockItem;
};

const ProductWarehouseArea = ({ record }: ProductWarehouseAreaProps) => {
  const { t } = useTranslation('product');
  const { id } = useParams();
  const { hasPermission } = useSecurity();
  const canWrite = hasPermission(STOCK_PERMISSIONS.STOCK_WRITE);

  const { updateWarehouseArea, isLoading: isSaving } = useProductStockUpdateWarehouseArea(
    id as string,
    record.warehouse,
  );

  const { data: areasData, isLoading: isLoadingAreas } = useQuery({
    queryKey: [WAREHOUSE_AREAS_LIST_KEY, 'all'],
    queryFn: () => WarehouseAreaService.searchClean({ size: 200 }),
    staleTime: 5 * 60 * 1000,
    enabled: canWrite,
  });

  const options: IWarehouseArea[] = areasData?.data ?? [];
  const currentValue = options.find((a) => a._id === record?.warehouseArea?.areaId) ?? null;

  const handleChange = (_: SyntheticEvent, newValue: IWarehouseArea | null) => {
    updateWarehouseArea(newValue?._id ?? null).catch(() => undefined);
  };

  if (!canWrite) {
    return <Typography variant='body2'>{record?.warehouseArea?.name ?? '—'}</Typography>;
  }

  return (
    <Autocomplete<IWarehouseArea>
      size='small'
      options={options}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(o, v) => o._id === v._id}
      value={currentValue}
      loading={isLoadingAreas || isSaving}
      onChange={handleChange}
      sx={{ minWidth: 180 }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('section.inventory.warehouseArea')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(isLoadingAreas || isSaving) && <CircularProgress size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default memo(ProductWarehouseArea);
